import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthState, User, LoginCredentials, RegisterData } from '@/types';
import { authService } from '@/services/authService';

interface AuthStore extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  clearAuth: () => void;
  lastRefreshTime: number;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      lastRefreshTime: 0,

      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true });
        try {
          const user = await authService.login(credentials.email, credentials.password);
          
          set({
            user: user,
            token: await authService.getToken(),
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error: any) {
          set({ isLoading: false });
          throw error;
        }
      },

      register: async (data: RegisterData) => {
        set({ isLoading: true });
        try {
          // Note: Register endpoint may not exist in backend, this is a placeholder
          throw new Error('Registration not implemented');
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          await authService.signOut();
        } catch (error) {
          console.warn('Logout error:', error);
        } finally {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
          // Force redirect to login page
          window.location.href = '/login';
        }
      },

      refreshUser: async () => {
        try {
          const state = get();
          const now = Date.now();
          
          // Prevent rapid successive calls (debounce for 5 seconds)
          if (state.isLoading || (now - state.lastRefreshTime) < 5000) {
            return;
          }
          
          set({ isLoading: true, lastRefreshTime: now });
          const user = await authService.fetchCurrentUser();
          if (user) {
            set({ user });
          }
        } catch (error) {
          console.error('Error refreshing user:', error);
          // Don't logout on refresh error, just log it
        } finally {
          set({ isLoading: false });
        }
      },

      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: { ...currentUser, ...userData },
          });
        }
      },

      clearAuth: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
